import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import Tasks from 'APP/Tasks';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';

interface ILayoutPresenter {
  group: Group | null;
  groupId: string | null;
  hasImage: boolean;
  messageId: string | null;
  imageUrl: string | null;
  preventInteraction: boolean;
  preventFocusOnClick: boolean;
  setMessageId(messageId: string): void;
  setImageUrl(imageUrl?: string): void;
  setPreventInteraction(preventInteraction: boolean): void;
  setPreventFocusOnClick(preventFocusOnClick: boolean): void;
  focusMessage(): void;
}

interface ILayoutPresenterProps {
  messageId: string;
  imageUrl?: string;
  onClick?(): void;
  preventInteraction?: boolean;
  preventFocusOnClick?: boolean;
}

export function useLayoutPresenter({
  messageId,
  imageUrl,
  onClick,
  preventInteraction,
  preventFocusOnClick,
}: ILayoutPresenterProps): ILayoutPresenter {
  const presenter = useLocalObservable<ILayoutPresenter>(() => ({
    messageId: null,
    imageUrl: null,
    preventInteraction: false,
    preventFocusOnClick: false,

    get group(): Group | null {
      if (!presenter.groupId) {
        return null;
      }
      return Entities.GroupsStore.getGroupById(presenter.groupId);
    },

    get groupId(): string | null {
      return Entities.GroupsStore.activeGroupId;
    },

    get hasImage(): boolean {
      return Boolean(presenter.imageUrl);
    },

    setMessageId(messageId: string): void {
      presenter.messageId = messageId;
    },

    setImageUrl(imageUrl?: string): void {
      presenter.imageUrl = imageUrl || null;
    },

    setPreventInteraction(preventInteraction: boolean): void {
      presenter.preventInteraction = preventInteraction;
    },

    setPreventFocusOnClick(preventFocusOnClick: boolean): void {
      presenter.preventFocusOnClick = preventFocusOnClick;
    },

    focusMessage(): void {
      if (presenter.preventInteraction) {
        return;
      }

      if (presenter.preventFocusOnClick) {
        onClick?.();
        return;
      }

      if (!presenter.groupId || !presenter.messageId) {
        return;
      }

      Tasks.messaging.focusMessage({
        groupId: presenter.groupId,
        messageId: presenter.messageId,
      });

      onClick?.();
    },
  }));

  useEffect(() => {
    presenter.setMessageId(messageId);
    presenter.setImageUrl(imageUrl);
    presenter.setPreventInteraction(!!preventInteraction);
    presenter.setPreventFocusOnClick(!!preventFocusOnClick);
  }, [messageId, imageUrl, preventInteraction, preventFocusOnClick]);

  return presenter;
}
